import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, ArrowLeft, CheckCircle, Battery, Zap, Shield, Car, Layers, Thermometer } from 'lucide-react';
import Header from '../../components/layout/Header';
import Footer from '../../components/layout/Footer';
import { Button } from '../../components/ui/button';

const SigenergyPage = () => {
  const features = [
    {
      icon: Layers,
      title: "5-in-1 Modular System",
      description: "Integrates solar inverter, battery storage, EV charger, smart energy management, and backup power in one sleek unit."
    },
    {
      icon: Battery,
      title: "LiFePO4 Technology",
      description: "Safe lithium iron phosphate cells with up to 10,000 cycles and 97% depth of discharge for maximum usable capacity."
    },
    {
      icon: Car,
      title: "Integrated EV Charging",
      description: "Optional 25kW DC bi-directional charger supporting V2H (Vehicle-to-Home) and V2G (Vehicle-to-Grid) capabilities."
    },
    {
      icon: Shield,
      title: "IP66 Weather Rating",
      description: "Dust and water resistant design built for Australian conditions, operating from -20°C to 55°C."
    }
  ];

  const modules = [
    {
      name: "BAT 5.0",
      capacity: "5.2 kWh",
      power: "2.5 kW",
      peak: "3.75 kW"
    },
    {
      name: "BAT 6.0",
      capacity: "5.84 kWh",
      power: "3 kW",
      peak: "4.5 kW"
    },
    {
      name: "BAT 8.0",
      capacity: "7.8 kWh",
      power: "4 kW",
      peak: "6 kW"
    },
    {
      name: "BAT 10.0",
      capacity: "8.76 kWh",
      power: "4.6 kW",
      peak: "6.9 kW"
    }
  ];

  const stats = [
    { value: "48 kWh", label: "Max Capacity Per Stack" },
    { value: "10,000", label: "Battery Cycles" },
    { value: "97%", label: "Depth of Discharge" }
  ];

  const whySigenergy = [
    "World's first 5-in-1 home energy solution",
    "Scalable from 5kWh to 48kWh per stack",
    "Silent operation with passive cooling (fanless)",
    "0ms backup switchover for seamless power",
    "5-layer safety protection system",
    "Built-in solar inverter and optimizer"
  ];

  return (
    <div className="min-h-screen">
      <Header />
      <main>
        {/* Hero Section */}
        <section className="pt-32 pb-16 bg-gradient-to-br from-[#f8faf5] via-white to-[#f0f7e6] relative overflow-hidden">
          <div className="absolute top-20 right-10 w-72 h-72 bg-[#B5D43B]/10 rounded-full blur-3xl" />
          <div className="absolute bottom-20 left-10 w-96 h-96 bg-[#3D5A47]/5 rounded-full blur-3xl" />
          
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
            {/* Breadcrumb */}
            <Link to="/brands" className="inline-flex items-center gap-2 text-[#3D5A47] hover:text-[#B5D43B] mb-8 transition-colors">
              <ArrowLeft className="w-4 h-4" />
              <span className="text-sm font-medium">Back to Trusted Brands</span>
            </Link>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
              <div>
                <div className="flex items-center gap-2 mb-4">
                  <Battery className="w-5 h-5 text-[#B5D43B]" />
                  <span className="text-sm font-semibold text-[#B5D43B]">All-in-One Energy System</span>
                </div>
                <h1 className="heading-1 mb-4">
                  <span className="text-[#3D5A47]">Sigenergy</span>
                </h1>
                <p className="text-xl text-[#3D5A47] font-medium mb-4">
                  The World's First 5-in-1 Home Energy Solution
                </p>
                <p className="body-large text-gray-600 mb-8">
                  Sigenergy SigenStor is a revolutionary modular all-in-one battery system that integrates solar inverters, LiFePO4 batteries, EV charging, and smart energy management into a single elegant unit.
                </p>
                <div className="flex flex-wrap gap-4">
                  <Link to="/contact">
                    <Button className="btn-primary gap-2">
                      Get a Sigenergy Quote
                      <ArrowRight className="w-4 h-4" />
                    </Button>
                  </Link>
                  <a href="https://www.sigenergy.com/" target="_blank" rel="noopener noreferrer">
                    <Button className="btn-secondary gap-2">
                      Visit Sigenergy Website
                      <ArrowRight className="w-4 h-4" />
                    </Button>
                  </a>
                </div>
              </div>
              <div className="flex justify-center">
                <div className="bg-white p-8 rounded-2xl shadow-xl border border-gray-100">
                  <img 
                    src="https://customer-assets.emergentagent.com/job_powerinnovate/artifacts/da5t6cpp_logo-sigenergy.png"
                    alt="Sigenergy Logo"
                    className="w-48 h-auto"
                  />
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Stats */}
        <section className="py-12 bg-[#3D5A47]">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8 text-center">
              {stats.map((stat, index) => (
                <div key={index}>
                  <p className="text-4xl font-bold text-[#B5D43B] mb-2">{stat.value}</p>
                  <p className="text-white/80">{stat.label}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Key Features */}
        <section className="py-16 bg-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <h2 className="heading-2 text-center mb-12">Why Choose Sigenergy?</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
              {features.map((feature, index) => (
                <div key={index} className="bg-gray-50 p-6 rounded-2xl hover:shadow-lg transition-shadow">
                  <div className="w-12 h-12 rounded-xl bg-[#B5D43B]/15 flex items-center justify-center mb-4">
                    <feature.icon className="w-6 h-6 text-[#3D5A47]" />
                  </div>
                  <h3 className="font-semibold text-[#3D5A47] mb-2">{feature.title}</h3>
                  <p className="text-gray-600 text-sm leading-relaxed">{feature.description}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Battery Modules */}
        <section className="py-16 bg-gray-50">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <h2 className="heading-2 text-center mb-4">Battery Module Options</h2>
            <p className="text-center text-gray-600 mb-12 max-w-2xl mx-auto">
              Mix and match modules to create the perfect system size for your needs. Stack up to 6 modules per tower.
            </p>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
              {modules.map((module, index) => (
                <div key={index} className="bg-white p-6 rounded-2xl border border-gray-100 shadow-sm hover:shadow-lg transition-shadow text-center">
                  <div className="w-16 h-16 rounded-full bg-[#B5D43B]/15 flex items-center justify-center mx-auto mb-4">
                    <Battery className="w-8 h-8 text-[#3D5A47]" />
                  </div>
                  <h3 className="font-bold text-[#3D5A47] text-lg mb-4">{module.name}</h3>
                  <div className="space-y-2 text-sm">
                    <div className="flex justify-between">
                      <span className="text-gray-500">Usable Capacity</span>
                      <span className="font-semibold text-[#3D5A47]">{module.capacity}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-500">Continuous Power</span>
                      <span className="font-semibold text-[#3D5A47]">{module.power}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-500">Peak Power (10s)</span>
                      <span className="font-semibold text-[#3D5A47]">{module.peak}</span>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* About Sigenergy */}
        <section className="py-16 bg-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
              <div>
                <h2 className="heading-2 mb-6">About Sigenergy</h2>
                <p className="text-gray-600 leading-relaxed mb-4">
                  Sigenergy is pioneering the future of home energy with their innovative SigenStor system - the world's first 5-in-1 residential energy solution that combines everything you need in one elegant package.
                </p>
                <p className="text-gray-600 leading-relaxed mb-4">
                  Using advanced LiFePO4 (Lithium Iron Phosphate) battery technology, Sigenergy systems offer exceptional safety, longevity, and performance. With up to 10,000 cycles and a 97% depth of discharge, you get maximum value from every kilowatt-hour stored.
                </p>
                <p className="text-gray-600 leading-relaxed">
                  The modular design means you can start small and expand as your needs grow, making it perfect for future-proofing your home's energy independence.
                </p>
              </div>
              <div className="space-y-3">
                <h3 className="font-semibold text-[#3D5A47] mb-4">Key Highlights</h3>
                {whySigenergy.map((item, index) => (
                  <div key={index} className="flex items-center gap-3 p-4 bg-gray-50 rounded-xl">
                    <CheckCircle className="w-5 h-5 text-[#B5D43B] flex-shrink-0" />
                    <span className="text-gray-700">{item}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* CTA */}
        <section className="py-16 bg-[#3D5A47]">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <h2 className="heading-2 text-white mb-4">Ready for Sigenergy?</h2>
            <p className="text-white/80 mb-8 max-w-2xl mx-auto">
              Experience the future of home energy with Sigenergy's revolutionary 5-in-1 system. Contact us for a personalised quote.
            </p>
            <Link to="/contact">
              <Button className="bg-[#B5D43B] hover:bg-[#a3c235] text-[#3D5A47] gap-2">
                Get Your Free Quote
                <ArrowRight className="w-4 h-4" />
              </Button>
            </Link>
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
};

export default SigenergyPage;
